import type { ContentTypeLink, Entry, ReferencedEntry, Sys } from 'contentful';
/**
 * Tries to get the value from the field for the object.
 *
 * @param obj any object
 * @param field field in obj
 * @param defaultProvider either a function that returns the default value, or the default value itself
 */
function get<O, K extends keyof O, R extends Required<O>>(
  obj: O | undefined,
  field: K,
  defaultProvider: (() => R[K]) | R[K],
): R[K];
function get<O, K extends keyof O>(obj: O | undefined, field: K): O[K] | undefined;
function get<O, K extends keyof O, R extends Required<O>>(
  obj: O | undefined,
  field: K,
  defaultProvider?: (() => R[K]) | R[K],
): O[K] | undefined {
  if (obj && obj[field] !== undefined) return obj[field];

  if (typeof defaultProvider === 'function') {
    return defaultProvider.call(undefined);
  }

  return defaultProvider;
}
export { get };

/**
 * Tries to get the value from the entry's field
 *
 * @param entry the contentful entry
 * @param field the field of that entry
 * @param defaultValue either a function that returns the default value, or the default value itself
 */
function getField<T, K extends keyof T, R extends Required<T>>(
  entry: ReferencedEntry<T> | undefined,
  field: K,
  defaultValue: (() => R[K]) | R[K],
): R[K];
function getField<T, K extends keyof T>(entry: ReferencedEntry<T> | undefined, field: K): T[K] | undefined;
function getField<T, K extends keyof T, R extends Required<T>>(
  entry: ReferencedEntry<T> | undefined,
  field: K,
  defaultValue?: (() => R[K]) | R[K],
): T[K] | undefined {
  return get(get(entry, 'fields'), field, defaultValue as unknown as any);
}
export { getField };

/**
 * Tries to get the sys field from the entry
 *
 * @param entry contentful entry
 * @param field sys field to retrieve
 */
export function getSys<K extends keyof Sys>(
  entry: ReferencedEntry<any> | undefined,
  field: K,
): Sys[K] | undefined {
  return get(get(entry, 'sys'), field);
}

/**
 * Gets the ContentTypeLink from the entry
 */
export function getContentTypeLink(entry?: ReferencedEntry<any>): ContentTypeLink | undefined {
  return get(getSys(entry, 'contentType'), 'sys');
}

/**
 * Calculates the component key; useful for rendering entry components in a loop
 */
export function componentKey<T>(entry: ReferencedEntry<T>, index: number) {
  return [getSys(entry, 'id'), index].join('.');
}

/**
 * Type guard that asserts the entry passed in is an Entry
 *
 * @param entry the contentful entry
 */
export function isEntry<T>(entry?: ReferencedEntry<T>): entry is Entry<T> {
  return !!(entry && entry.fields);
}

/**
 * Type guard that asserts the entry passed in is an Entry an has sys defined
 *
 * @param entry the contentful entry
 */
export function validate<T>(entry?: ReferencedEntry<T>): entry is Entry<T> {
  return !!(isEntry(entry) && entry.sys);
}
